import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { submitLead, type LeadPayload } from '../services/leads'

type Props = {
  // Variant lets the form render as a dark hero panel ("hero"), a
  // light inline card ("card"), or a compact one-line strip suitable
  // for sticky mobile CTAs ("compact"). Default is "card".
  variant?: 'hero' | 'card' | 'compact'
  className?: string
  // Hint that gets passed straight to `Market.from_city` on the server.
  // Not surfaced as a field — visitors enter their own city below.
  defaultCity?: string
  // Override the lead origin (e.g. "chat" when posted from the chat
  // widget's callback CTA). Defaults to server-side "web".
  source?: string
  onSuccess?: (leadId: number) => void
}

export default function LeadCaptureForm({
  variant = 'card',
  className = '',
  defaultCity = '',
  source,
  onSuccess,
}: Props) {
  const [form, setForm] = useState<LeadPayload>({
    first_name:   '',
    last_name:    '',
    email:        '',
    phone:        '',
    pet_name:     '',
    pet_species:  '',
    address_city: defaultCity,
    notes:        '',
  })
  const [submitted, setSubmitted] = useState(false)

  const mutation = useMutation({
    mutationFn: () => submitLead({ ...form, ...(source ? { source } : {}) }),
    onSuccess: (res) => {
      setSubmitted(true)
      onSuccess?.(res.lead_id)
    },
  })

  function handleChange(field: keyof LeadPayload, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.first_name?.trim()) return
    if (!form.email?.trim() && !form.phone?.trim()) return
    mutation.mutate()
  }

  const isHero    = variant === 'hero'
  const isCompact = variant === 'compact'

  // Style maps so we can reuse the form chrome on a dark hero, a
  // light landing card, or a compact mobile strip without duplicating
  // markup.
  const styles = isHero
    ? {
        wrap:   'rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 p-5',
        title:  'text-white font-semibold text-base mb-1',
        sub:    'text-brand-200 text-xs mb-4',
        label:  'sr-only',
        input:  'w-full rounded-lg px-3 py-2 bg-white/95 text-gray-900 placeholder-gray-400 text-sm border border-white/30 focus:outline-none focus:ring-2 focus:ring-coral-400',
        button: 'w-full rounded-lg bg-coral-500 hover:bg-coral-600 text-white font-semibold text-sm py-2.5 transition-colors disabled:opacity-50',
        success: 'text-white text-sm',
        error:   'text-coral-200 text-xs',
      }
    : isCompact
    ? {
        wrap:   'rounded-xl bg-white border border-gray-200 p-3 shadow-sm',
        title:  'text-gray-900 font-semibold text-sm mb-0.5',
        sub:    'text-gray-500 text-xs mb-3',
        label:  'sr-only',
        input:  'input',
        button: 'btn-primary w-full text-sm',
        success: 'text-gray-700 text-sm',
        error:   'text-red-600 text-xs',
      }
    : {
        wrap:   'card',
        title:  'text-gray-900 font-semibold text-lg mb-1',
        sub:    'text-gray-500 text-sm mb-4',
        label:  'sr-only',
        input:  'input',
        button: 'btn-primary w-full',
        success: 'text-gray-700 text-sm',
        error:   'text-red-600 text-xs',
      }

  if (submitted) {
    return (
      <div className={`${styles.wrap} ${className}`}>
        <p className={styles.title}>Thanks — we&apos;ll be in touch! 🐾</p>
        <p className={styles.sub}>
          A care coordinator will reach out shortly to help you book your visit.
        </p>
      </div>
    )
  }

  // Compact layout: name + phone + button on a single line. Used for
  // sticky mobile CTAs where vertical space is at a premium.
  if (isCompact) {
    return (
      <form onSubmit={handleSubmit} className={`${styles.wrap} ${className}`} noValidate>
        <p className={styles.title}>Get a callback</p>
        <p className={styles.sub}>Drop your details and a care coordinator will reach out.</p>
        <div className="grid grid-cols-1 gap-2">
          <input
            className={styles.input}
            required
            placeholder="Your name *"
            value={form.first_name}
            onChange={(e) => handleChange('first_name', e.target.value)}
            autoComplete="given-name"
          />
          <input
            type="tel"
            className={styles.input}
            placeholder="Phone *"
            value={form.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            autoComplete="tel"
          />
          <input
            className={styles.input}
            placeholder="City"
            value={form.address_city}
            onChange={(e) => handleChange('address_city', e.target.value)}
            autoComplete="address-level2"
          />
          {mutation.isError && (
            <p className={styles.error}>
              Couldn&apos;t submit just now. Please try again.
            </p>
          )}
          <button
            type="submit"
            disabled={
              mutation.isPending ||
              !form.first_name?.trim() ||
              (!form.email?.trim() && !form.phone?.trim())
            }
            className={styles.button}
          >
            {mutation.isPending ? 'Sending…' : 'Request a callback'}
          </button>
        </div>
      </form>
    )
  }

  return (
    <form onSubmit={handleSubmit} className={`${styles.wrap} ${className}`} noValidate>
      <p className={styles.title}>Talk to a care coordinator</p>
      <p className={styles.sub}>
        Tell us a bit about your pet and we&apos;ll reach out to book your first visit.
      </p>

      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <input
            id="lead-first-name"
            className={styles.input}
            required
            placeholder="First name *"
            value={form.first_name}
            onChange={(e) => handleChange('first_name', e.target.value)}
            autoComplete="given-name"
            aria-label="First name"
          />
          <input
            id="lead-last-name"
            className={styles.input}
            placeholder="Last name"
            value={form.last_name}
            onChange={(e) => handleChange('last_name', e.target.value)}
            autoComplete="family-name"
            aria-label="Last name"
          />
        </div>

        <input
          id="lead-email"
          type="email"
          className={styles.input}
          placeholder="Email"
          value={form.email}
          onChange={(e) => handleChange('email', e.target.value)}
          autoComplete="email"
          aria-label="Email"
        />

        <input
          id="lead-phone"
          type="tel"
          className={styles.input}
          placeholder="Phone (we'll text you to confirm)"
          value={form.phone}
          onChange={(e) => handleChange('phone', e.target.value)}
          autoComplete="tel"
          aria-label="Phone"
        />

        <div className="grid grid-cols-3 gap-2">
          <input
            className={styles.input}
            placeholder="Pet's name"
            value={form.pet_name}
            onChange={(e) => handleChange('pet_name', e.target.value)}
            autoComplete="off"
            aria-label="Pet's name"
          />
          <select
            className={styles.input}
            value={form.pet_species}
            onChange={(e) => handleChange('pet_species', e.target.value)}
            aria-label="Pet species"
          >
            <option value="">Species</option>
            <option value="dog">Dog</option>
            <option value="cat">Cat</option>
            <option value="rabbit">Rabbit</option>
            <option value="bird">Bird</option>
            <option value="other">Other</option>
          </select>
          <input
            className={styles.input}
            placeholder="City"
            value={form.address_city}
            onChange={(e) => handleChange('address_city', e.target.value)}
            autoComplete="address-level2"
            aria-label="City"
          />
        </div>

        <textarea
          className={styles.input}
          placeholder="Anything we should know about your pet? (optional)"
          rows={2}
          value={form.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          aria-label="Notes"
        />

        {mutation.isError && (
          <p className={styles.error}>
            Couldn&apos;t submit just now. Please try again, or call us directly.
          </p>
        )}

        <button
          type="submit"
          disabled={mutation.isPending || !form.first_name?.trim() || (!form.email?.trim() && !form.phone?.trim())}
          className={`${styles.button} mt-2`}
        >
          {mutation.isPending ? 'Sending…' : 'Get in touch'}
        </button>

        {!isHero && (
          <p className="text-[11px] text-gray-400 text-center mt-1">
            By submitting, you agree to be contacted about Nala services. We&apos;ll never spam you.
          </p>
        )}
      </div>
    </form>
  )
}
